import { z } from "zod";

// ============================================================================
// AI Providers
// ============================================================================

const emptyToUndefined = z.literal("").transform(() => undefined);
const coercedNumber = z.union([emptyToUndefined, z.coerce.number()]);

export const createProviderSchema = z.object({
  id: z
    .string()
    .min(1, "ID is required")
    .regex(/^[a-z0-9-]+$/, "ID must be lowercase alphanumeric with hyphens"),
  name: z.string().min(1, "Name is required"),
  adapterType: z.string().min(1, "Adapter type is required"),
  apiKey: z.string().min(1, "API key is required"),
  baseUrl: z.string().url("Must be a valid URL"),
  rpmLimit: coercedNumber.pipe(z.number().int().positive().optional()),
  rpsLimit: coercedNumber.pipe(z.number().int().positive().optional()),
});

export const updateProviderSchema = z.object({
  name: z.string().min(1, "Name is required"),
  baseUrl: z.string().url("Must be a valid URL"),
});

export const updateProviderKeySchema = z.object({
  apiKey: z.string().min(1, "API key is required"),
});

export const updateRateLimitsSchema = z.object({
  rpmLimit: coercedNumber.pipe(z.number().int().positive().optional()),
  rpsLimit: coercedNumber.pipe(z.number().int().positive().optional()),
});

// ============================================================================
// AI Secrets
// ============================================================================

export const createSecretSchema = z.object({
  id: z
    .string()
    .min(1, "ID is required")
    .regex(/^[a-z0-9_-]+$/, "ID must be lowercase alphanumeric with hyphens/underscores"),
  category: z.string().min(1, "Category is required"),
  name: z.string().min(1, "Name is required"),
  value: z.string().min(1, "Value is required"),
  description: z.string().optional(),
  isSecret: z.boolean().default(true),
});

export const updateSecretSchema = z.object({
  value: z.string().min(1, "Value is required"),
});

// ============================================================================
// AI Model Pricing
// ============================================================================

export const createPricingSchema = z.object({
  providerId: z.string().min(1, "Provider is required"),
  model: z.string().min(1, "Model name is required"),
  inputPricePerMToken: coercedNumber.pipe(z.number().min(0, "Must be >= 0")),
  outputPricePerMToken: coercedNumber.pipe(z.number().min(0, "Must be >= 0")),
  cachedInputPricePerMToken: coercedNumber.pipe(z.number().min(0, "Must be >= 0").optional()),
});

export const updatePricingSchema = z.object({
  inputPricePerMToken: coercedNumber.pipe(z.number().min(0, "Must be >= 0")),
  outputPricePerMToken: coercedNumber.pipe(z.number().min(0, "Must be >= 0")),
  cachedInputPricePerMToken: coercedNumber.pipe(z.number().min(0, "Must be >= 0").optional()),
});

// ============================================================================
// AI Prompts
// ============================================================================

const isProviderBrandedPromptId = (promptId: string, providerId: string): boolean => {
  const providerToken = providerId.trim().toLowerCase();
  const promptToken = promptId.trim().toLowerCase();

  if (!providerToken || !promptToken) return false;
  if (promptToken === providerToken) return true;
  if (promptToken.startsWith(`${providerToken}-`)) return true;
  if (promptToken.endsWith(`-${providerToken}`)) return true;
  return promptToken.includes(`-${providerToken}-`);
};

export const createPromptSchema = z
  .object({
    id: z
      .string()
      .min(1, "ID is required")
      .regex(/^[a-z0-9-]+$/, "ID must be lowercase alphanumeric with hyphens"),
    providerId: z.string().min(1, "Provider is required"),
    model: z.string().min(1, "Model is required"),
    systemPrompt: z.string().min(1, "System prompt is required"),
    userPromptTemplate: z.string().optional(),
    temperature: coercedNumber.pipe(z.number().min(0).max(2).optional()),
    maxTokens: coercedNumber.pipe(z.number().int().positive().optional()),
    topP: coercedNumber.pipe(z.number().min(0).max(1).optional()),
    schemaRef: z.string().optional(),
  })
  .refine((value) => !isProviderBrandedPromptId(value.id, value.providerId), {
    message: "Prompt ID must be provider-agnostic and cannot include the provider brand",
    path: ["id"],
  });

export const createPromptVersionSchema = z.object({
  providerId: z.string().min(1, "Provider is required"),
  model: z.string().min(1, "Model is required"),
  systemPrompt: z.string().min(1, "System prompt is required"),
  userPromptTemplate: z.string().optional(),
  temperature: coercedNumber.pipe(z.number().min(0).max(2).optional()),
  maxTokens: coercedNumber.pipe(z.number().int().positive().optional()),
  topP: coercedNumber.pipe(z.number().min(0).max(1).optional()),
  schemaRef: z.string().optional(),
});

// ============================================================================
// Inferred Types
// ============================================================================

export type CreateProviderInput = z.infer<typeof createProviderSchema>;
export type UpdateProviderInput = z.infer<typeof updateProviderSchema>;
export type UpdateProviderKeyInput = z.infer<typeof updateProviderKeySchema>;
export type UpdateRateLimitsInput = z.infer<typeof updateRateLimitsSchema>;
export type CreateSecretInput = z.infer<typeof createSecretSchema>;
export type UpdateSecretInput = z.infer<typeof updateSecretSchema>;
export type CreatePricingInput = z.infer<typeof createPricingSchema>;
export type UpdatePricingInput = z.infer<typeof updatePricingSchema>;
export type CreatePromptInput = z.infer<typeof createPromptSchema>;
export type CreatePromptVersionInput = z.infer<typeof createPromptVersionSchema>;
